import React, { useState, useEffect } from 'react';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Send as SendIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';

const DirectRequestButton = ({ individualId, individualName, variant = 'contained', size = 'medium' }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [managers, setManagers] = useState([]);

  const [formData, setFormData] = useState({
    request_type: 'task',
    subject: '',
    message: '',
    priority: 'normal',
    assigned_to: ''
  });

  useEffect(() => {
    if (open) {
      fetchManagers();
    }
  }, [open]);

  const fetchManagers = async () => {
    try {
      const response = await fetchWithAuth('/api/employees');
      if (response.ok) {
        const data = await response.json();
        // Filter to show managers and administrators
        // Only those with user accounts can be assigned (others shown as disabled)
        const managersAndAdmins = data.filter(emp =>
          emp.position?.toLowerCase().includes('manager') ||
          emp.position?.toLowerCase().includes('administrator') ||
          emp.position?.toLowerCase().includes('admin')
        );
        setManagers(managersAndAdmins);
      }
    } catch (err) {
      console.error('Error fetching managers:', err);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setSuccess(false);
    setError(null);
    setFormData({
      request_type: 'task',
      subject: '',
      message: '',
      priority: 'normal',
      assigned_to: ''
    });
  };

  const handleClose = () => {
    setOpen(false);
    setSuccess(false);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!formData.subject.trim()) {
      setError('Subject is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetchWithAuth('/api/direct-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          individual_id: individualId,
          ...formData,
          assigned_to: formData.assigned_to || null
        })
      });

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => {
          handleClose();
        }, 1500);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to create request');
      }
    } catch (err) {
      console.error('Error creating request:', err);
      setError('Failed to create request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const requestTypes = [
    { value: 'task', label: 'General Task' },
    { value: 'document', label: 'Document Request' },
    { value: 'meeting', label: 'Meeting Request' },
    { value: 'other', label: 'Other' }
  ];

  const priorities = [
    { value: 'low', label: 'Low', color: 'default' },
    { value: 'normal', label: 'Normal', color: 'primary' },
    { value: 'high', label: 'High', color: 'warning' },
    { value: 'urgent', label: 'Urgent', color: 'error' }
  ];

  return (
    <>
      <Button
        variant={variant}
        size={size}
        startIcon={<AssignmentIcon />}
        onClick={handleOpen}
        color="secondary"
      >
        Request
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentIcon color="primary" />
              <Typography variant="h6">Create Direct Request</Typography>
            </Box>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          {individualName && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              For: <strong>{individualName}</strong>
            </Typography>
          )}
        </DialogTitle>

        <DialogContent dividers>
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Request created successfully!
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Request Type</InputLabel>
              <Select
                value={formData.request_type}
                onChange={(e) => setFormData({ ...formData, request_type: e.target.value })}
                label="Request Type"
              >
                {requestTypes.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              size="small"
              label="Subject"
              required
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="Brief description of the request"
            />

            <TextField
              fullWidth
              size="small"
              label="Message"
              multiline
              rows={4}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Additional details about the request..."
            />

            <FormControl fullWidth size="small">
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                label="Priority"
                renderValue={(value) => {
                  const priority = priorities.find(p => p.value === value);
                  return (
                    <Chip
                      size="small"
                      label={priority?.label}
                      color={priority?.color}
                    />
                  );
                }}
              >
                {priorities.map(priority => (
                  <MenuItem key={priority.value} value={priority.value}>
                    <Chip size="small" label={priority.label} color={priority.color} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Assign To (Optional)</InputLabel>
              <Select
                value={formData.assigned_to}
                onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                label="Assign To (Optional)"
              >
                <MenuItem value="">
                  <em>Unassigned</em>
                </MenuItem>
                {managers.map(manager => (
                  <MenuItem
                    key={manager.id}
                    value={manager.user_account_id || ''}
                    disabled={!manager.user_account_id}
                  >
                    {manager.name} {manager.position && `(${manager.position})`}
                    {!manager.user_account_id && ' - No account yet'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !formData.subject.trim()}
            startIcon={loading ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {loading ? 'Sending...' : 'Send Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DirectRequestButton;
